import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AlertTitle
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  InsertDriveFile as InsertDriveFileIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { api } from '../services/api';

const EmailConfiguration = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [smtpSettings, setSmtpSettings] = useState({});
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Email Composer State
  const [composerData, setComposerData] = useState({
    reason: '',
    recipients: [],
    subject: '',
    message: '',
    template_id: '',
    custom_data: {}
  });

  // Template Management State
  const [templateDialog, setTemplateDialog] = useState({ open: false, mode: 'add', template: {} });
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '' });

  // SMTP Settings State
  const [smtpForm, setSmtpForm] = useState({
    host: '',
    port: '',
    secure: true,
    username: '',
    password: '',
    from_email: '',
    from_name: ''
  });

  // Available reasons for emails
  const emailReasons = [
    'Welcome Email',
    'Weekly Reminder',
    'Project Update',
    'Timesheet Reminder',
    'Performance Review',
    'Training Notification',
    'Meeting Invitation',
    'Custom Message'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, smtpRes, logsRes] = await Promise.all([
        api.get('/api/email-templates'),
        api.get('/api/smtp-settings'),
        api.get('/api/email-logs')
      ]);
      setTemplates(templatesRes.data);
      setSmtpSettings(smtpRes.data || {});
      setEmailLogs(logsRes.data);
      
      // Initialize SMTP form with current settings
      if (smtpRes.data) {
        setSmtpForm({
          host: smtpRes.data.host || '',
          port: smtpRes.data.port || '',
          secure: smtpRes.data.secure === 1,
          username: smtpRes.data.username || '',
          password: smtpRes.data.password || '',
          from_email: smtpRes.data.from_email || '',
          from_name: smtpRes.data.from_name || ''
        });
      }
    } catch (error) {
      showSnackbar('Failed to load email configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Email Composer Functions
  const handleComposerChange = (field, value) => {
    setComposerData(prev => ({ ...prev, [field]: value }));
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setComposerData(prev => ({
        ...prev,
        template_id: templateId,
        subject: template.subject,
        message: template.body
      }));
    }
  };

  const sendEmail = async () => {
    let recipients = [];
    if (composerData.recipients) {
      if (Array.isArray(composerData.recipients)) {
        recipients = composerData.recipients;
      } else {
        recipients = [composerData.recipients];
      }
    }
    
    if (!recipients.length || !composerData.subject || !composerData.message) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/send-email', {
        ...composerData,
        recipients: recipients
      });
      showSnackbar('Email sent successfully!');
      setComposerData({
        reason: '',
        recipients: [],
        subject: '',
        message: '',
        template_id: '',
        custom_data: {}
      });
      loadData(); // Refresh logs
    } catch (error) {
      showSnackbar('Failed to send email', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Template Management Functions
  const openTemplateDialog = (mode = 'add', template = {}) => {
    setTemplateDialog({ open: true, mode, template });
    setTemplateForm({
      name: template.name || '',
      subject: template.subject || '',
      body: template.body || ''
    });
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      showSnackbar('Please fill in all template fields', 'error');
      return;
    }

    setLoading(true);
    try {
      if (templateDialog.mode === 'add') {
        await api.post('/api/email-templates', templateForm);
        showSnackbar('Template created successfully!');
      } else {
        await api.put(`/api/email-templates/${templateDialog.template.id}`, templateForm);
        showSnackbar('Template updated successfully!');
      }
      setTemplateDialog({ open: false, mode: 'add', template: {} });
      loadData();
    } catch (error) {
      showSnackbar('Failed to save template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    setLoading(true);
    try {
      await api.delete(`/api/email-templates/${templateId}`);
      showSnackbar('Template deleted successfully!');
      loadData();
    } catch (error) {
      showSnackbar('Failed to delete template', 'error');
    } finally {
      setLoading(false);
    }
  };

  // SMTP Settings Functions
  const saveSmtpSettings = async () => {
    if (!smtpForm.host || !smtpForm.port || !smtpForm.from_email) {
      showSnackbar('Please fill in all required SMTP fields', 'error');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/smtp-settings', smtpForm);
      showSnackbar('SMTP settings saved successfully!');
      loadData();
    } catch (error) {
      showSnackbar('Failed to save SMTP settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const testSmtpConnection = async () => {
    setLoading(true);
    try {
      await api.post('/api/smtp-test', smtpForm);
      showSnackbar('SMTP connection test successful!');
    } catch (error) {
      showSnackbar('SMTP connection test failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <CheckCircleIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      default: return <WarningIcon color="warning" />;
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Email Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage email templates, SMTP settings, and send emails to employees
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<EmailIcon />} label="Email Composer" />
          <Tab icon={<InsertDriveFileIcon />} label="Templates" />
          <Tab icon={<SettingsIcon />} label="SMTP Settings" />
          <Tab icon={<HistoryIcon />} label="Email Logs" />
        </Tabs>
      </Paper>

      {/* Email Composer Tab */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Compose Email
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Reason</InputLabel>
                <Select
                  value={composerData.reason}
                  onChange={(e) => handleComposerChange('reason', e.target.value)}
                  label="Reason"
                >
                  {emailReasons.map((reason) => (
                    <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Template</InputLabel>
                <Select
                  value={composerData.template_id}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  label="Template"
                >
                  <MenuItem value="">No Template</MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Recipients (comma-separated emails)"
                value={(() => {
                  const recipients = composerData.recipients;
                  if (!recipients) return '';
                  if (Array.isArray(recipients)) {
                    return recipients.join(', ');
                  }
                  return recipients;
                })()}
                onChange={(e) => {
                  const emails = e.target.value.split(',').map(email => email.trim()).filter(email => email.length > 0);
                  handleComposerChange('recipients', emails);
                }}
                placeholder="email1@example.com, email2@example.com"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={composerData.subject}
                onChange={(e) => handleComposerChange('subject', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Message"
                value={composerData.message}
                onChange={(e) => handleComposerChange('message', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => setComposerData({
                    reason: '',
                    recipients: [],
                    subject: '',
                    message: '',
                    template_id: '',
                    custom_data: {}
                  })}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={sendEmail}
                  disabled={loading}
                >
                  Send Email
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Templates Tab */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Email Templates</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openTemplateDialog('add')}
            >
              Add Template
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>{template.subject}</TableCell>
                    <TableCell>{new Date(template.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => openTemplateDialog('edit', template)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => deleteTemplate(template.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* SMTP Settings Tab */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            SMTP Configuration
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SMTP Host"
                value={smtpForm.host}
                onChange={(e) => setSmtpForm(prev => ({ ...prev, host: e.target.value }))}
                placeholder="smtp.gmail.com"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Port"
                type="number"
                value={smtpForm.port}
                onChange={(e) => setSmtpForm(prev => ({ ...prev, port: e.target.value }))}
                placeholder="587"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={smtpForm.username}
                onChange={(e) => setSmtpForm(prev => ({ ...prev, username: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={smtpForm.password}
                onChange={(e) => setSmtpForm(prev => ({ ...prev, password: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Email"
                value={smtpForm.from_email}
                onChange={(e) => setSmtpForm(prev => ({ ...prev, from_email: e.target.value }))}
                placeholder="noreply@company.com"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Name"
                value={smtpForm.from_name}
                onChange={(e) => setSmtpForm(prev => ({ ...prev, from_name: e.target.value }))}
                placeholder="PM TimeSheet System"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smtpForm.secure}
                    onChange={(e) => setSmtpForm(prev => ({ ...prev, secure: e.target.checked }))}
                  />
                }
                label="Use TLS/SSL"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={testSmtpConnection}
                  disabled={loading}
                >
                  Test Connection
                </Button>
                <Button
                  variant="contained"
                  onClick={saveSmtpSettings}
                  disabled={loading}
                >
                  Save Settings
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Email Logs Tab */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Email History
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{new Date(log.sent_at).toLocaleString()}</TableCell>
                    <TableCell>{log.subject}</TableCell>
                    <TableCell>{log.recipients}</TableCell>
                    <TableCell>{log.template_name || 'Custom'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon(log.status)}
                        {log.status}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Template Dialog */}
      <Dialog open={templateDialog.open} onClose={() => setTemplateDialog({ open: false, mode: 'add', template: {} })} maxWidth="md" fullWidth>
        <DialogTitle>
          {templateDialog.mode === 'add' ? 'Add Template' : 'Edit Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateForm.name}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Body"
                value={templateForm.body}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, body: e.target.value }))}
                helperText="Use {{employee_name}} for dynamic content"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog({ open: false, mode: 'add', template: {} })}>
            Cancel
          </Button>
          <Button onClick={saveTemplate} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EmailConfiguration; 